import { supabase } from '../supabaseClient';
import { sendNewProjectNotification } from './email';
import { createNewProjectNotification } from './notifications';

// Types
export interface Project {
  id: string;
  client_id: string;
  title: string;
  description: string;
  budget_min: number | null;
  budget_max: number | null;
  deadline: string | null;
  category_id: string | null;
  skills: string[];
  status: 'DRAFT' | 'PENDING' | 'PUBLISHED' | 'CLOSED' | 'CANCELLED';
  featured: boolean;
  views_count: number;
  offers_count: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectWithDetails extends Project {
  client: {
    id: string;
    full_name: string | null;
    company: string | null;
    avatar_url: string | null;
    rating_avg: number;
    rating_cnt: number;
    verified: boolean;
  };
  category: {
    id: string;
    name: string;
    description: string | null;
  } | null;
  offers?: Offer[];
}

export interface Offer {
  id: string;
  project_id: string;
  provider_id: string;
  message: string;
  price: number;
  delivery_days: number;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectFilters {
  search?: string;
  category_id?: string;
  budget_min?: number;
  budget_max?: number;
  skills?: string[];
  status?: string;
  featured?: boolean;
  sort_by?: 'created_at' | 'budget_min' | 'deadline' | 'views_count' | 'offers_count' | 'budget_min-asc' | 'budget_min-desc' | 'views_count-desc' | 'offers_count-desc' | 'created_at-asc' | 'created_at-desc';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Get all published projects with filters
export const getProjects = async (filters: ProjectFilters = {}): Promise<{
  projects: ProjectWithDetails[];
  total: number;
  page: number;
  totalPages: number;
}> => {
  try {
    const {
      search = '',
      category_id,
      budget_min,
      budget_max,
      skills = [],
      featured,
      sort_by = 'created_at',
      sort_order = 'desc',
      page = 1,
      limit = 20
    } = filters;

    let query = supabase
      .from('projects')
      .select(`
        *,
        client:profiles!projects_client_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified
        ),
        category:categories(
          id,
          name,
          description
        )
      `)
      .eq('status', 'PUBLISHED');

    // Apply filters
    if (search) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    if (category_id) {
      query = query.eq('category_id', category_id);
    }

    if (budget_min !== undefined) {
      query = query.gte('budget_min', budget_min);
    }

    if (budget_max !== undefined) {
      query = query.lte('budget_max', budget_max);
    }

    if (skills.length > 0) {
      query = query.overlaps('skills', skills);
    }

    if (featured !== undefined) {
      query = query.eq('featured', featured);
    }

    // Apply sorting
    query = query.order(sort_by, { ascending: sort_order === 'asc' });

    // Apply pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }

    const total = count || 0;
    const totalPages = Math.ceil(total / limit);

    return {
      projects: data || [],
      total,
      page,
      totalPages
    };
  } catch (error) {
    console.error('Error in getProjects:', error);
    throw error;
  }
};


// Get all categories
export const getCategories = async (): Promise<Category[]> => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getCategories:', error);
    return [];
  }
};

// Create new project
export const createProject = async (projectData: {
  title: string;
  description: string;
  budget_min?: number;
  budget_max?: number;
  deadline?: string;
  category_id?: string;
  skills?: string[];
}): Promise<Project> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { data, error } = await supabase
      .from('projects')
      .insert([{
        ...projectData,
        client_id: user.id,
        status: 'PENDING',
        skills: projectData.skills || []
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating project:', error);
      throw error;
    }

    // Send email and in-app notifications to all freelancers and agencies about new project
    try {
      // Get category name if category_id is provided
      let categoryName = '';
      if (projectData.category_id) {
        const { data: category } = await supabase
          .from('categories')
          .select('name')
          .eq('id', projectData.category_id)
          .single();
        categoryName = category?.name || '';
      }

      // Send email notifications
      await sendNewProjectNotification(
        data.id,
        data.title,
        data.description,
        data.budget_min,
        data.budget_max,
        categoryName,
        data.deadline,
        data.skills
      );

      // Send in-app notifications
      await createNewProjectNotification(
        data.id,
        data.title,
        data.description,
        data.budget_min,
        data.budget_max,
        categoryName
      );
    } catch (notificationError) {
      console.error('Notification sending error:', notificationError);
      // Don't fail project creation if notifications fail
    }

    return data;
  } catch (error) {
    console.error('Error in createProject:', error);
    throw error;
  }
};

// Update project
export const updateProject = async (projectId: string, updates: Partial<Project>): Promise<Project> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .select()
      .single();

    if (error) {
      console.error('Error updating project:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateProject:', error);
    throw error;
  }
};

// Delete project
export const deleteProject = async (projectId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteProject:', error);
    throw error;
  }
};

// Submit offer
export const submitOffer = async (offerData: {
  project_id: string;
  message: string;
  price: number;
  delivery_days: number;
}): Promise<Offer> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { data, error } = await supabase
      .from('offers')
      .insert([{
        ...offerData,
        provider_id: user.id
      }])
      .select()
      .single();

    if (error) {
      console.error('Error submitting offer:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in submitOffer:', error);
    throw error;
  }
};

// Get user's projects
export const getUserProjects = async (userId?: string): Promise<Project[]> => {
  try {
    let query = supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('client_id', userId);
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }
      query = query.eq('client_id', user.id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user projects:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserProjects:', error);
    return [];
  }
};

// Get user's offers
export const getUserOffers = async (userId?: string): Promise<Offer[]> => {
  try {
    let query = supabase
      .from('offers')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('provider_id', userId);
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }
      query = query.eq('provider_id', user.id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user offers:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserOffers:', error);
    return [];
  }
};

// Increment project views
export const incrementProjectViews = async (projectId: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_project_views', {
      project_id: projectId
    });

    if (error) {
      console.error('Error incrementing project views:', error);
      // Don't throw error for analytics
    }
  } catch (error) {
    console.error('Error in incrementProjectViews:', error);
    // Don't throw error for analytics
  }
};

// Get featured projects
export const getFeaturedProjects = async (limit: number = 6): Promise<ProjectWithDetails[]> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:profiles!projects_client_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified
        ),
        category:categories(
          id,
          name,
          description
        )
      `)
      .eq('status', 'PUBLISHED')
      .eq('featured', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching featured projects:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getFeaturedProjects:', error);
    return [];
  }
};

// Get project by ID
export const getProjectById = async (id: string): Promise<ProjectWithDetails> => {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:profiles!projects_client_id_fkey(
          id,
          full_name,
          company,
          avatar_url,
          rating_avg,
          rating_cnt,
          verified
        ),
        category:categories(
          id,
          name,
          description
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching project:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Project not found');
    }

    // Increment views
    await incrementProjectViews(id);

    return data;
  } catch (error) {
    console.error('Error in getProjectById:', error);
    throw error;
  }
};

// Promotion management functions
export const promoteProject = async (projectId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('projects')
      .update({ featured: true })
      .eq('id', projectId);

    if (error) throw error;
  } catch (error) {
    console.error('Error promoting project:', error);
    throw error;
  }
};

export const unpromoteProject = async (projectId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('projects')
      .update({ featured: false })
      .eq('id', projectId);

    if (error) throw error;
  } catch (error) {
    console.error('Error unpromoting project:', error);
    throw error;
  }
};

export const toggleProjectPromotion = async (projectId: string, featured: boolean): Promise<void> => {
  try {
    const { error } = await supabase
      .from('projects')
      .update({ featured })
      .eq('id', projectId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling project promotion:', error);
    throw error;
  }
};